import React, { useState, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSecurityIncidents } from '../../modules/audit/hooks';
import { useAuditSnapshots } from '../../modules/audit/hooks';
import {
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
  SecurityIncident,
  SEVERITY_COLORS,
  SEVERITY_DOT,
  SEVERITY_LABEL,
  STATUS_COLORS,
  STATUS_LABEL,
  INCIDENT_TYPE_LABEL,
} from '../../modules/audit/models';
import {
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Filter,
  RefreshCw,
  ChevronDown,
  Search,
  Eye,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// ── helpers ──────────────────────────────────────────────────────────────────

function formatResolutionTime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isWithinLastNDays(iso: string, days: number): boolean {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(iso).getTime() >= cutoff;
}

// ── blank form state ──────────────────────────────────────────────────────────

interface CreateForm {
  title: string;
  description: string;
  incident_type: IncidentType;
  severity: IncidentSeverity;
  affected_systems_raw: string;
}

const BLANK_FORM: CreateForm = {
  title: '',
  description: '',
  incident_type: 'suspicious_login',
  severity: 'medium',
  affected_systems_raw: '',
};

// ── component ─────────────────────────────────────────────────────────────────

export default function SecurityIncidents() {
  const { user } = useAuth();

  // filter bar state
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'all'>('all');

  // modals
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<SecurityIncident | null>(null);
  const [editStatus, setEditStatus] = useState<IncidentStatus>('open');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusSaveError, setStatusSaveError] = useState<string | null>(null);

  // create form
  const [createForm, setCreateForm] = useState<CreateForm>(BLANK_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // hook filters — only pass defined values so the hook dependency stays stable
  const hookFilters = useMemo(() => {
    const f: { severity?: IncidentSeverity; status?: IncidentStatus } = {};
    if (severityFilter !== 'all') f.severity = severityFilter;
    if (statusFilter !== 'all') f.status = statusFilter;
    return f;
  }, [severityFilter, statusFilter]);

  const { incidents, loading, error, refetch, createIncident, updateStatus } =
    useSecurityIncidents(hookFilters);

  const { snapshots } = useAuditSnapshots();

  // ── derived stats ────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    // use all incidents from the hook (already filtered server-side by severity/status)
    // but we need "total open" regardless of current filter, so we compute from
    // the unfiltered list. The hook re-fetches on filter change, so we derive
    // stats from the current `incidents` array which reflects the active filters.
    // For a clean stats row we show counts from the currently returned data.
    const totalOpen = incidents.filter(
      (i) => i.status === 'open' || i.status === 'investigating',
    ).length;

    const criticalHighActive = incidents.filter(
      (i) =>
        (i.severity === 'critical' || i.severity === 'high') &&
        (i.status === 'open' || i.status === 'investigating'),
    ).length;

    const resolved = incidents.filter((i) => i.resolved_at && i.created_at);
    const avgResolutionMs =
      resolved.length > 0
        ? resolved.reduce((sum, i) => {
            return (
              sum +
              (new Date(i.resolved_at!).getTime() - new Date(i.created_at).getTime())
            );
          }, 0) / resolved.length
        : null;

    const thisWeek = incidents.filter((i) => isWithinLastNDays(i.created_at, 7)).length;

    return { totalOpen, criticalHighActive, avgResolutionMs, thisWeek };
  }, [incidents]);

  // ── client-side search filter ────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.toLowerCase();
    return incidents.filter(
      (i) =>
        i.title.toLowerCase().includes(q) ||
        (i.description ?? '').toLowerCase().includes(q),
    );
  }, [incidents, search]);

  // ── create incident ──────────────────────────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    let affectedSystems: Record<string, unknown> | null = null;
    if (createForm.affected_systems_raw.trim()) {
      try {
        affectedSystems = JSON.parse(createForm.affected_systems_raw);
        if (!Array.isArray(affectedSystems) && typeof affectedSystems !== 'object') {
          throw new Error('Must be a JSON object or array');
        }
      } catch {
        setCreateError('Affected Systems must be valid JSON (object or array).');
        return;
      }
    }

    setCreating(true);
    try {
      await createIncident({
        title: createForm.title.trim(),
        description: createForm.description.trim() || null,
        incident_type: createForm.incident_type,
        severity: createForm.severity,
        status: 'open',
        affected_systems: affectedSystems,
        affected_user_ids: null,
        ip_address: null,
        geolocation: null,
        action_history: null,
        rollback_available: false,
        root_cause: null,
        timeline_history: null,
        related_log_ids: null,
        resolved_at: null,
        resolved_by: null,
      });
      setCreateForm(BLANK_FORM);
      setShowCreate(false);
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create incident.');
    } finally {
      setCreating(false);
    }
  };

  // ── update status ────────────────────────────────────────────────────────

  const handleSaveStatus = async () => {
    if (!selectedIncident) return;
    setSavingStatus(true);
    setStatusSaveError(null);
    try {
      await updateStatus(selectedIncident.id, editStatus, user?.id);
      setSelectedIncident((prev) => prev ? { ...prev, status: editStatus } : null);
    } catch (err) {
      setStatusSaveError(err instanceof Error ? err.message : 'Failed to update status.');
    } finally {
      setSavingStatus(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#40E0D0]" />
              Security Incidents
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Monitor and respond to security events
            </p>
          </div>
          <button
            onClick={() => { setCreateForm(BLANK_FORM); setCreateError(null); setShowCreate(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] hover:bg-[#38cdc0] text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Incident
          </button>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* ── Stats row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
            iconBg="bg-red-50"
            label="Total Open"
            value={loading ? '—' : String(stats.totalOpen)}
          />
          <StatCard
            icon={<Shield className="w-5 h-5 text-orange-500" />}
            iconBg="bg-orange-50"
            label="Critical / High Active"
            value={loading ? '—' : String(stats.criticalHighActive)}
          />
          <StatCard
            icon={<Clock className="w-5 h-5 text-teal-600" />}
            iconBg="bg-teal-50"
            label="Avg Resolution Time"
            value={
              loading
                ? '—'
                : stats.avgResolutionMs !== null
                ? formatResolutionTime(stats.avgResolutionMs)
                : 'N/A'
            }
          />
          <StatCard
            icon={<CheckCircle className="w-5 h-5 text-blue-500" />}
            iconBg="bg-blue-50"
            label="Incidents This Week"
            value={loading ? '—' : String(stats.thisWeek)}
            sub={`${snapshots.length} snapshot${snapshots.length !== 1 ? 's' : ''}`}
          />
        </div>

        {/* ── Filter bar ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by title or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
              />
            </div>

            {/* Severity filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as IncidentSeverity | 'all')}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] appearance-none bg-white"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as IncidentStatus | 'all')}
                className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] appearance-none bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Refresh */}
            <button
              onClick={refetch}
              title="Refresh"
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40E0D0]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <Shield className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-gray-500 font-medium">No incidents found</p>
              <p className="text-gray-400 text-sm mt-1">
                {search || severityFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters.'
                  : 'Create a new incident to get started.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50">
                    {['Severity', 'Title', 'Type', 'Status', 'Affected Systems', 'Created', 'Actions'].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((incident) => (
                    <tr key={incident.id} className="hover:bg-gray-50 transition-colors">
                      {/* Severity */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${SEVERITY_COLORS[incident.severity]}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[incident.severity]}`}
                          />
                          {SEVERITY_LABEL[incident.severity]}
                        </span>
                      </td>

                      {/* Title + description */}
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {incident.title}
                        </p>
                        {incident.description && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {incident.description}
                          </p>
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                        {INCIDENT_TYPE_LABEL[incident.incident_type]}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[incident.status]}`}
                        >
                          {STATUS_LABEL[incident.status]}
                        </span>
                      </td>

                      {/* Affected systems count */}
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                        {incident.affected_systems
                          ? Array.isArray(incident.affected_systems)
                            ? incident.affected_systems.length
                            : Object.keys(incident.affected_systems).length
                          : 0}
                      </td>

                      {/* Created at */}
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {formatDateShort(incident.created_at)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedIncident(incident);
                            setEditStatus(incident.status);
                            setStatusSaveError(null);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:border-[#40E0D0] hover:text-[#40E0D0] transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Incident Modal ── */}
      {showCreate && (
        <Modal
          title="New Security Incident"
          onClose={() => setShowCreate(false)}
        >
          <form onSubmit={handleCreate} className="space-y-4">
            {createError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {createError}
              </div>
            )}

            <Field label="Title" required>
              <input
                type="text"
                required
                value={createForm.title}
                onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Brief incident title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
              />
            </Field>

            <Field label="Description">
              <textarea
                rows={3}
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Describe what happened..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] resize-none"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Incident Type" required>
                <div className="relative">
                  <select
                    required
                    value={createForm.incident_type}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        incident_type: e.target.value as IncidentType,
                      }))
                    }
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] appearance-none bg-white"
                  >
                    {(Object.keys(INCIDENT_TYPE_LABEL) as IncidentType[]).map((k) => (
                      <option key={k} value={k}>
                        {INCIDENT_TYPE_LABEL[k]}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </Field>

              <Field label="Severity" required>
                <div className="relative">
                  <select
                    required
                    value={createForm.severity}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        severity: e.target.value as IncidentSeverity,
                      }))
                    }
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] appearance-none bg-white"
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </Field>
            </div>

            <Field label="Affected Systems (JSON array)">
              <textarea
                rows={2}
                value={createForm.affected_systems_raw}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, affected_systems_raw: e.target.value }))
                }
                placeholder='e.g. ["auth", "billing", "api"]'
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] resize-none"
              />
            </Field>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#40E0D0] hover:bg-[#38cdc0] text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creating && (
                  <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                )}
                Create Incident
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── View / Update Modal ── */}
      {selectedIncident && (
        <Modal
          title="Incident Details"
          onClose={() => setSelectedIncident(null)}
          wide
        >
          <div className="space-y-5">
            {/* Header badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_COLORS[selectedIncident.severity]}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[selectedIncident.severity]}`}
                />
                {SEVERITY_LABEL[selectedIncident.severity]}
              </span>
              <span
                className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[selectedIncident.status]}`}
              >
                {STATUS_LABEL[selectedIncident.status]}
              </span>
              <span className="text-xs text-gray-400">
                {INCIDENT_TYPE_LABEL[selectedIncident.incident_type]}
              </span>
            </div>

            {/* Title */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Title
              </p>
              <p className="text-sm text-gray-900 font-medium">{selectedIncident.title}</p>
            </div>

            {/* Description */}
            {selectedIncident.description && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Description
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedIncident.description}
                </p>
              </div>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-3">
              <DetailItem label="Created" value={formatDate(selectedIncident.created_at)} />
              <DetailItem label="Updated" value={formatDate(selectedIncident.updated_at)} />
              {selectedIncident.resolved_at && (
                <DetailItem
                  label="Resolved"
                  value={formatDate(selectedIncident.resolved_at)}
                />
              )}
              {selectedIncident.ip_address && (
                <DetailItem label="IP Address" value={selectedIncident.ip_address} />
              )}
              {selectedIncident.rollback_available && (
                <DetailItem label="Rollback Available" value="Yes" />
              )}
            </div>

            {/* Affected systems */}
            {selectedIncident.affected_systems && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Affected Systems
                </p>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-28">
                  {JSON.stringify(selectedIncident.affected_systems, null, 2)}
                </pre>
              </div>
            )}

            {/* Root cause */}
            {selectedIncident.root_cause && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Root Cause
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedIncident.root_cause}
                </p>
              </div>
            )}

            {/* Timeline */}
            {selectedIncident.timeline_history &&
              Object.keys(selectedIncident.timeline_history).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Timeline
                  </p>
                  <TimelineList data={selectedIncident.timeline_history} />
                </div>
              )}

            {/* Action history */}
            {selectedIncident.action_history &&
              Object.keys(selectedIncident.action_history).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Action History
                  </p>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto max-h-28">
                    {JSON.stringify(selectedIncident.action_history, null, 2)}
                  </pre>
                </div>
              )}

            {/* Status update */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Update Status
              </p>
              {statusSaveError && (
                <div className="flex items-start gap-2 mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {statusSaveError}
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as IncidentStatus)}
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] appearance-none bg-white"
                  >
                    <option value="open">Open</option>
                    <option value="investigating">Investigating</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <button
                  onClick={handleSaveStatus}
                  disabled={savingStatus || editStatus === selectedIncident.status}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#40E0D0] hover:bg-[#38cdc0] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingStatus && (
                    <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                  )}
                  Save Status
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={() => setSelectedIncident(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({
  icon,
  iconBg,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
      <div className={`${iconBg} p-3 rounded-lg flex-shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800 font-medium">{value}</p>
    </div>
  );
}

function TimelineList({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return null;

  return (
    <ul className="space-y-2">
      {entries.map(([key, val]) => (
        <li key={key} className="flex gap-3 text-sm">
          <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-teal-400" />
          <div className="min-w-0">
            <span className="font-medium text-gray-700">{key}:</span>{' '}
            <span className="text-gray-500">
              {typeof val === 'string' ? val : JSON.stringify(val)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
